import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { systemLogsApi } from '../../utils/api/systemLogs.js';
import { extractPaginatedList, normalizePagination } from '../../utils/pagination.js';
import { getErrorMessage } from '../../utils/api/errors.js';

const initialState = {
  logs: [],
  currentLog: null,
  stats: null,
  services: [],
  loading: false,
  statsLoading: false,
  error: null,
  filters: {
    page: 1,
    limit: 25,
    type: 'all',
    service: 'all',
    search: '',
    dateRange: 'all',
  },
  pagination: {
    total: 0,
    page: 1,
    limit: 25,
    totalPages: 0,
  },
};

export const fetchSystemLogs = createAsyncThunk(
  'systemLogs/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await systemLogsApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch system logs'));
    }
  }
);

export const fetchSystemLogById = createAsyncThunk(
  'systemLogs/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await systemLogsApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch system log'));
    }
  }
);

export const fetchSystemLogStats = createAsyncThunk(
  'systemLogs/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await systemLogsApi.getStats();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch log stats'));
    }
  }
);

export const fetchSystemLogServices = createAsyncThunk(
  'systemLogs/fetchServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await systemLogsApi.getServices();
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch services'));
    }
  }
);

export const exportSystemLogs = createAsyncThunk(
  'systemLogs/export',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await systemLogsApi.export(params);
      return response;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to export logs'));
    }
  }
);

const systemLogsSlice = createSlice({
  name: 'systemLogs',
  initialState,
  reducers: {
    clearCurrentLog: (state) => {
      state.currentLog = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemLogs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemLogs.fulfilled, (state, action) => {
        state.loading = false;
        const { data, pagination } = extractPaginatedList(action.payload);
        state.logs = data;
        if (pagination) {
          state.pagination = normalizePagination(pagination, state.pagination);
        }
      })
      .addCase(fetchSystemLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.logs = [];
      })

      .addCase(fetchSystemLogById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemLogById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLog = action.payload;
      })
      .addCase(fetchSystemLogById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.currentLog = null;
      })

      .addCase(fetchSystemLogStats.pending, (state) => {
        state.statsLoading = true;
      })
      .addCase(fetchSystemLogStats.fulfilled, (state, action) => {
        state.statsLoading = false;
        state.stats = action.payload;
      })
      .addCase(fetchSystemLogStats.rejected, (state, action) => {
        state.statsLoading = false;
      })

      .addCase(fetchSystemLogServices.fulfilled, (state, action) => {
        state.services = Array.isArray(action.payload) ? action.payload : [];
      })

      .addCase(exportSystemLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(exportSystemLogs.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(exportSystemLogs.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  clearCurrentLog,
  clearError,
  setFilters,
  resetFilters,
  setPage,
} = systemLogsSlice.actions;

export default systemLogsSlice.reducer;
